import React, { useEffect, useState } from 'react';
import {
  Paper, Box, Typography, Button, Table, TableBody, TableCell, TableContainer, TableHead, TableRow,
  Dialog, DialogTitle, DialogContent, DialogActions, TextField, Chip, Alert, Switch, FormControlLabel,
  CircularProgress,
} from '@mui/material';
import { doc, onSnapshot } from 'firebase/firestore';
import { getFunctions, httpsCallable } from 'firebase/functions';
import { db } from '../firebase';

const GOLD = '#C9A84C';
const NAVY = '#0D1E35';
const HOLDER_TYPES = ['member', 'parent', 'coach', 'renter', 'event', 'other'];
const KINDS = [['card', 'Card'], ['fob', 'Key fob'], ['other', 'Other']];
const kindName = (k) => (KINDS.find(([v]) => v === (k || 'card')) || KINDS[0])[1];

// RFID cards for the gate keypad. A card works like a PIN (same holder, same
// windows, same member standing check, same log) but nobody types its number:
// "Enroll a card" arms the keypad, and the next unknown card tapped there in
// the next three minutes is bound to the chosen holder. The page watches
// gateCardEnrollment/current to say when that happened. A card can also be
// enrolled from the number printed on it, and any printed number can be looked
// up to see what the keypad reads it as — the "card …1234" of a refused tap.
export default function GateCardsPanel({ users, displayName, onMessage }) {
  const functions = getFunctions();
  const [cards, setCards] = useState([]);
  const [loaded, setLoaded] = useState(false);
  const [showRevoked, setShowRevoked] = useState(false);
  const [busy, setBusy] = useState('');
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(null);
  const [enrollment, setEnrollment] = useState(null);
  const [now, setNow] = useState(Date.now());
  const [lookup, setLookup] = useState('');
  const [lookupResult, setLookupResult] = useState(null);

  const load = async (withRevoked = showRevoked) => {
    try {
      const { data } = await httpsCallable(functions, 'listGateCards')({ includeRevoked: withRevoked });
      setCards(data?.cards || []);
      setLoaded(true);
    } catch (e) {
      onMessage({ severity: 'error', text: `Could not load cards: ${e.message}` });
    }
  };

  useEffect(() => {
    load(false);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => onSnapshot(doc(db, 'gateCardEnrollment', 'current'),
    snap => setEnrollment(snap.exists() ? snap.data() : null),
    () => setEnrollment(null)), []);

  // A countdown while the keypad is waiting for a card.
  const waiting = enrollment?.status === 'waiting'
    && (enrollment.expiresAt?.toMillis?.() || 0) > now;
  useEffect(() => {
    if (!waiting) return undefined;
    const t = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(t);
  }, [waiting]);

  // Refresh the list the moment a card lands.
  const finishedKey = enrollment?.status === 'done' ? enrollment.cardId : '';
  useEffect(() => {
    if (finishedKey) load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [finishedKey]);

  const openDialog = () => {
    setForm({ holderType: 'member', holderId: '', holderName: '', label: '', kind: 'card', userSearch: '', expiresAt: '', notes: '', how: 'tap', number: '' });
    setOpen(true);
  };

  const start = async () => {
    const f = form;
    if (!f.holderId && !f.label.trim()) {
      onMessage({ severity: 'warning', text: 'Pick an account, or give the card a label so the log can name it.' });
      return;
    }
    if (f.how === 'number' && !f.number.trim()) {
      onMessage({ severity: 'warning', text: 'Type the number printed on the card.' });
      return;
    }
    setBusy('start');
    try {
      const rules = f.expiresAt ? { expiresAt: new Date(`${f.expiresAt}T23:59:59`).toISOString() } : {};
      const who = {
        holderType: f.holderType, holderId: f.holderId || null,
        label: f.label.trim() || f.holderName, kind: f.kind, rules, notes: f.notes.trim(),
      };
      if (f.how === 'number') {
        const { data } = await httpsCallable(functions, 'enrollCardByNumber')({ ...who, number: f.number.trim() });
        setOpen(false);
        onMessage({ severity: 'success', text: `${kindName(f.kind)} ${data.cardHint} enrolled for ${who.label}. Tap it once at the keypad to be sure it reads the same.` });
        await load();
      } else {
        await httpsCallable(functions, 'startCardEnrollment')(who);
        setNow(Date.now());
        setOpen(false);
      }
    } catch (e) {
      onMessage({ severity: 'error', text: `Could not enroll: ${e.message}` });
    }
    setBusy('');
  };

  const lookUp = async () => {
    if (!lookup.trim()) return;
    setBusy('lookup');
    try {
      const { data } = await httpsCallable(functions, 'lookupCardNumber')({ number: lookup.trim() });
      setLookupResult(data);
    } catch (e) {
      setLookupResult(null);
      onMessage({ severity: 'error', text: `Could not look that up: ${e.message}` });
    }
    setBusy('');
  };

  const cancel = async () => {
    setBusy('cancel');
    try { await httpsCallable(functions, 'cancelCardEnrollment')({}); } catch (e) { /* the doc says what happened */ }
    setBusy('');
  };

  const revoke = async (c) => {
    if (!window.confirm(`Revoke the ${kindName(c.kind).toLowerCase()} ${c.cardHint || ''} for ${c.label}? It stops opening the gate immediately.`)) return;
    setBusy(c.id);
    try {
      await httpsCallable(functions, 'revokeGateCard')({ cardId: c.id, reason: 'revoked from the admin page' });
      await load();
    } catch (e) {
      onMessage({ severity: 'error', text: `Could not revoke that card: ${e.message}` });
    }
    setBusy('');
  };

  // An outcome stays on screen for ten minutes, not forever: the doc keeps the
  // last enrollment, and a week-old "enrolled" banner would only confuse.
  const recent = (enrollment?.finishedAt?.toMillis?.() || 0) > now - 10 * 60 * 1000;
  const secondsLeft = waiting ? Math.max(0, Math.round(((enrollment.expiresAt?.toMillis?.() || 0) - now) / 1000)) : 0;
  const fmt = (iso) => (iso ? new Date(iso).toLocaleString('en-US', { month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit' }) : '—');
  const cell = { color: 'rgba(255,255,255,0.85)', borderColor: 'rgba(255,255,255,0.08)' };
  const head = { color: GOLD, fontWeight: 700, borderColor: 'rgba(255,255,255,0.12)' };

  return (
    <Paper sx={{ p: 2, mb: 2, backgroundColor: 'rgba(255,255,255,0.05)', border: '1px solid rgba(201,168,76,0.25)' }}>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, flexWrap: 'wrap', mb: 1.5 }}>
        <Typography sx={{ color: GOLD, fontWeight: 700, flex: 1 }}>RFID cards</Typography>
        <FormControlLabel
          control={<Switch size="small" checked={showRevoked}
            onChange={e => { setShowRevoked(e.target.checked); load(e.target.checked); }} />}
          label={<span style={{ color: 'rgba(255,255,255,0.7)', fontSize: '0.8rem' }}>Show revoked</span>}
        />
        <Button size="small" variant="contained" onClick={openDialog} disabled={waiting}
          sx={{ backgroundColor: GOLD, color: NAVY, fontWeight: 700 }}>
          Enroll a card
        </Button>
      </Box>

      {waiting && (
        <Alert severity="info" sx={{ mb: 1.5 }} icon={<CircularProgress size={18} />}
          action={<Button size="small" color="inherit" disabled={busy === 'cancel'} onClick={cancel}>Cancel</Button>}>
          Tap the {kindName(enrollment.kind).toLowerCase()} for <strong>{enrollment.label}</strong> on the keypad now — {Math.floor(secondsLeft / 60)}:
          {String(secondsLeft % 60).padStart(2, '0')} left. The keypad gives two long beeps when it takes it.
        </Alert>
      )}
      {recent && enrollment?.status === 'done' && (
        <Alert severity="success" sx={{ mb: 1.5 }}>
          {kindName(enrollment.kind)} {enrollment.cardHint} enrolled for <strong>{enrollment.label}</strong>.
        </Alert>
      )}
      {recent && enrollment?.status === 'duplicate' && (
        <Alert severity="warning" sx={{ mb: 1.5 }}>
          That card ({enrollment.cardHint}) is already enrolled{enrollment.existingLabel ? ` for ${enrollment.existingLabel}` : ''}.
          Revoke it first to give it to someone else, then enroll again.
        </Alert>
      )}
      {enrollment?.status === 'waiting' && !waiting && (
        <Alert severity="warning" sx={{ mb: 1.5 }}>No card was tapped in time. Enroll again when the card is at the keypad.</Alert>
      )}

      <Typography sx={{ color: 'rgba(255,255,255,0.5)', fontSize: '0.75rem', mb: 1 }}>
        A card opens the gate for its holder like their PIN does, and a member's card stops working with the
        membership. Cards are easy to copy, so they suit regulars; anyone on a tight window is better on a PIN.
      </Typography>

      {/* For a refused tap: the gate log shows "card …1234", and this says
          which printed number that is and whose card it belongs to. */}
      <Box sx={{ display: 'flex', gap: 1, alignItems: 'center', flexWrap: 'wrap', mb: 1.5 }}>
        <TextField size="small" placeholder="Look up a printed card number" value={lookup}
          onChange={e => { setLookup(e.target.value); setLookupResult(null); }}
          onKeyDown={e => { if (e.key === 'Enter') lookUp(); }}
          sx={{ minWidth: 240, '& .MuiInputBase-root': { color: 'white', fontSize: '0.85rem' },
            '& .MuiOutlinedInput-notchedOutline': { borderColor: 'rgba(255,255,255,0.25)' } }} />
        <Button size="small" variant="outlined" disabled={busy === 'lookup' || !lookup.trim()} onClick={lookUp}
          sx={{ color: GOLD, borderColor: 'rgba(201,168,76,0.5)' }}>Look up</Button>
        {lookupResult && (
          <Typography sx={{ color: 'rgba(255,255,255,0.8)', fontSize: '0.8rem' }}>
            {!lookupResult.ok ? 'Not a card number.'
              : `Reads as ${lookupResult.cardHint} at the keypad — `
                + (lookupResult.enrolled
                  ? `${kindName(lookupResult.enrolled.kind).toLowerCase()} for ${lookupResult.enrolled.label}${lookupResult.enrolled.enabled ? '' : ' (revoked)'}.`
                  : 'not enrolled.')}
          </Typography>
        )}
      </Box>

      <TableContainer>
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell sx={head}>Holder</TableCell>
              <TableCell sx={head}>Type</TableCell>
              <TableCell sx={head}>Card</TableCell>
              <TableCell sx={head}>Window</TableCell>
              <TableCell sx={head}>Last used</TableCell>
              <TableCell sx={head} />
            </TableRow>
          </TableHead>
          <TableBody>
            {cards.map(c => (
              <TableRow key={c.id} sx={{ opacity: c.enabled === false ? 0.5 : 1 }}>
                <TableCell sx={cell}>{c.label}</TableCell>
                <TableCell sx={cell}>{c.holder?.type}</TableCell>
                <TableCell sx={cell}>{kindName(c.kind)} {c.cardHint || ''}</TableCell>
                <TableCell sx={cell}>{c.windowText}</TableCell>
                <TableCell sx={cell}>{fmt(c.lastUsed)}{c.useCount ? ` · ${c.useCount}×` : ''}</TableCell>
                <TableCell sx={cell} align="right">
                  {c.enabled === false ? (
                    <Chip size="small" label={c.revokedReason ? `revoked — ${c.revokedReason}` : 'revoked'} />
                  ) : (
                    <Button size="small" color="error" disabled={busy === c.id} onClick={() => revoke(c)}>Revoke</Button>
                  )}
                </TableCell>
              </TableRow>
            ))}
            {loaded && cards.length === 0 && (
              <TableRow>
                <TableCell colSpan={6} sx={{ ...cell, color: 'rgba(255,255,255,0.5)' }}>
                  No cards yet. Enroll one above with the card in hand at the keypad.
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </TableContainer>

      <Dialog open={open} onClose={() => setOpen(false)} fullWidth maxWidth="sm">
        <DialogTitle>Enroll an RFID card</DialogTitle>
        <DialogContent>
          {form && (
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 1 }}>
              <TextField select size="small" label="Who is this for" value={form.holderType}
                onChange={e => setForm(f => ({ ...f, holderType: e.target.value }))}
                SelectProps={{ native: true }}>
                {HOLDER_TYPES.map(t => <option key={t} value={t}>{t}</option>)}
              </TextField>
              <Box>
                <TextField fullWidth size="small" label="Search for an account"
                  value={form.userSearch}
                  onChange={e => setForm(f => ({ ...f, userSearch: e.target.value, holderId: '', holderName: '' }))}
                  placeholder="Name or email" />
                {form.holderId ? (
                  <Typography variant="caption" sx={{ color: '#27AE60', fontWeight: 700 }}>
                    Attached to {form.holderName}
                  </Typography>
                ) : form.userSearch.trim().length >= 2 && (
                  <Box sx={{ mt: 0.5, maxHeight: 150, overflowY: 'auto' }}>
                    {users
                      .filter(u => `${displayName(u)} ${u.email || ''}`.toLowerCase().includes(form.userSearch.trim().toLowerCase()))
                      .slice(0, 6)
                      .map(u => (
                        <Button key={u.id} size="small" fullWidth
                          sx={{ justifyContent: 'flex-start', textTransform: 'none' }}
                          onClick={() => setForm(f => ({
                            ...f, holderId: u.id, holderName: displayName(u),
                            label: f.label || displayName(u), userSearch: displayName(u),
                          }))}>
                          {displayName(u)} — {u.email}
                        </Button>
                      ))}
                  </Box>
                )}
              </Box>
              <TextField select size="small" label="How" value={form.how}
                onChange={e => setForm(f => ({ ...f, how: e.target.value }))}
                SelectProps={{ native: true }}>
                <option value="tap">Tap it at the keypad</option>
                <option value="number">Type the number printed on it</option>
              </TextField>
              {form.how === 'number' && (
                <TextField size="small" label="Printed number" value={form.number}
                  onChange={e => setForm(f => ({ ...f, number: e.target.value }))}
                  placeholder="0008505158, or 129,51014"
                  helperText="The 10-digit number, or the pair beside it. Both work." />
              )}
              <TextField select size="small" label="Card or fob" value={form.kind}
                onChange={e => setForm(f => ({ ...f, kind: e.target.value }))}
                SelectProps={{ native: true }}>
                {KINDS.map(([v, n]) => <option key={v} value={v}>{n}</option>)}
              </TextField>
              <TextField size="small" label="Label (what the log will show)" value={form.label}
                onChange={e => setForm(f => ({ ...f, label: e.target.value }))} />
              <TextField size="small" type="date" label="Expires (optional)" InputLabelProps={{ shrink: true }}
                value={form.expiresAt} onChange={e => setForm(f => ({ ...f, expiresAt: e.target.value }))} />
              <TextField size="small" label="Notes" value={form.notes}
                onChange={e => setForm(f => ({ ...f, notes: e.target.value }))} />
              <Typography variant="caption" sx={{ color: '#888' }}>
                {form.how === 'number'
                  ? 'The card works as soon as you press Enroll. Some sellers print a number that differs from the chip, so tap it once at the keypad to be sure. '
                  : 'After you press Start, tap the card on the keypad within three minutes. '}
                A member card follows the membership: frozen or lapsed, it is refused; cancelled, it is revoked.
              </Typography>
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpen(false)}>Cancel</Button>
          <Button variant="contained" disabled={busy === 'start'} onClick={start}
            sx={{ backgroundColor: GOLD, color: NAVY, fontWeight: 700 }}>
            {busy === 'start' ? 'Saving…' : form?.how === 'number' ? 'Enroll' : 'Start'}
          </Button>
        </DialogActions>
      </Dialog>
    </Paper>
  );
}
